import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Table, Form, Nav, Tab, Card, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import objToQueryString from '../helpFunctions/objToQueryParameters';
import getUnique from '../helpFunctions/getUnique'
import Loading from '../components/Loading';
import EloLeaderboard from '../components/EloLeaderboard';
import ReferenceResults from '../components/ReferenceResults';
import queryString from 'query-string';
import { authenticatedFetch } from '../helpFunctions/authFetch';
import { getEnvVariable } from '../helpFunctions/getEnvVariable';
import './results.css';

// Add jStat library for statistical calculations - you'll need to install this with npm
// npm install jstat
import jStat from 'jstat';

const Results = () => {
    const [votes, setVotes] = useState([]);
    const [uniqueCategories, setUniqueCategories] = useState({});
    const [weightedResults, setWeightedResults] = useState(null);
    const [isLoadingWeighted, setIsLoadingWeighted] = useState(false);
    const [filteredWeightedResults, setFilteredWeightedResults] = useState(null);
    const [heatmapColorScheme, setHeatmapColorScheme] = useState('viridis'); // Add color scheme state
    const [comparisonScheme, setComparisonScheme] = useState('all_pairs');
    const [referenceVariantId, setReferenceVariantId] = useState(null);
    const [referenceVariantName, setReferenceVariantName] = useState(null);
    const [comparisonVariants, setComparisonVariants] = useState([]);
    const [activeTab, setActiveTab] = useState('analysis');

    const location = useLocation();
    const { surveyId } = useParams(); // Extract survey ID from URL

    const navigate = useNavigate();

    useEffect(() => {
        let url = `${getEnvVariable('API_BASE_URL')}results/${surveyId}`
        authenticatedFetch(navigate, url)
            .then(res => {
                if (!res.ok) {
                    throw new Error(`HTTP error! status: ${res.status}`);
                }
                return res.json();
            })
            .then((votesResponse) => {
                if (Array.isArray(votesResponse) && votesResponse.length > 0) {
                    setVotes(votesResponse);

                    const filter = queryString.parse(location.search);

                    const variantsById = new Map();
                    votesResponse.forEach((vote) => {
                        if (vote.variant_id_winner !== null && vote.variant_id_winner !== undefined) {
                            variantsById.set(String(vote.variant_id_winner), {
                                variant: vote.variant_id_winner,
                                variantName: vote.winner_variant_name,
                            });
                        }
                        if (vote.variant_id_loser !== null && vote.variant_id_loser !== undefined) {
                            variantsById.set(String(vote.variant_id_loser), {
                                variant: vote.variant_id_loser,
                                variantName: vote.loser_variant_name,
                            });
                        }
                    });

                    setUniqueCategories({
                        variant: [...variantsById.values()].map((variant) => ({
                            ...variant,
                            isActive: filter.variant ? !filter.variant.split('-').includes(String(variant.variant)) : true
                        })),
                        user: getUnique('user_name', votesResponse).map(user => ({user: user,
                            isActive: filter.user ? !filter.user.split('-').includes(String(user)) : true})),
                        device: getUnique('device', votesResponse).map(device => ({device: device,
                            isActive: filter.device ? !filter.device.split('-').includes(device) : true})),
                        resolution: getUnique('screen_resolution', votesResponse).map(resolution => ({resolution: resolution,
                            isActive: filter.resolution ? !filter.resolution.split('-').includes(resolution) : true})),
                        startDate: filter.startDate ? filter.startDate : findMinDate(votesResponse.map(vote => vote.vote_timestamp)),
                        endDate: filter.endDate ? filter.endDate : findMaxDate(votesResponse.map(vote => vote.vote_timestamp))
                        });

                    // Automatically fetch weighted results after getting basic votes
                    fetchWeightedResults();
                } else {
                    setVotes([]);
                    setUniqueCategories({
                        variant: [],
                        user: [],
                        device: [],
                        resolution: [],
                        startDate: "",
                        endDate: "",
                    });
                    fetchWeightedResults();
                }
            })
            .catch(error => {
                console.error('Error fetching results:', error);
                alert('Failed to fetch results. Please try again.');
            });
    }, [surveyId, location.search])

    // Function to fetch weighted analysis results
    const fetchWeightedResults = () => {
        setIsLoadingWeighted(true);
        let url = `${getEnvVariable('API_BASE_URL')}weighted_results/${surveyId}`
        authenticatedFetch(navigate, url)
            .then(res => {
                if (!res.ok) {
                    throw new Error(`HTTP error! status: ${res.status}`);
                }
                return res.json();
            })
            .then((data) => {
                setWeightedResults(data);
                const nextComparisonScheme = data.comparison_scheme || 'all_pairs';
                setComparisonScheme(nextComparisonScheme);
                setReferenceVariantId(data.reference_variant_id ?? null);
                setReferenceVariantName(data.reference_variant_name || null);
                const activeVariants = data.active_variants || [];
                setComparisonVariants(activeVariants);
                const filter = queryString.parse(location.search);
                setUniqueCategories((currentCategories) => {
                    if (!currentCategories.variant) return currentCategories;

                    const variantsById = new Map(
                        currentCategories.variant.map((variant) => [
                            String(variant.variant),
                            variant,
                        ])
                    );
                    activeVariants.forEach((variant) => {
                        const key = String(variant.variant_id);
                        if (!variantsById.has(key)) {
                            variantsById.set(key, {
                                variant: variant.variant_id,
                                variantName: variant.variant_name,
                                isActive: filter.variant
                                    ? !filter.variant.split('-').includes(key)
                                    : true,
                            });
                        }
                    });
                    return {
                        ...currentCategories,
                        variant: [...variantsById.values()],
                    };
                });
                setActiveTab(nextComparisonScheme === 'reference_vs_all' ? 'reference' : 'analysis');
                setIsLoadingWeighted(false);
            })
            .catch(error => {
                console.error('Error fetching weighted results:', error);
                setIsLoadingWeighted(false);
            });
    };

    function findMinDate(dates) {
        // Convert each date string to a Date object and find the minimum date
        const minDate = new Date(Math.min(...dates.map(date => new Date(date))));

        // Format the minimum date back to 'yyyy-mm-dd'
        return minDate.toISOString().slice(0, 10);
    }

    function findMaxDate(dates) {
        const maxDate = new Date(Math.max(...dates.map(date => new Date(date))));

        // Increase the date by one day
        maxDate.setDate(maxDate.getDate() + 1);

        return maxDate.toISOString().slice(0, 10);
    }


    // Function to count votes for a specific variantId
    const countVotesForVariant = (votes, variantId) => {
        return votes.reduce((count, vote) => {
            // Count if this variant participated in the vote (either as winner or loser)
            const shouldCount = (vote.variant_id_winner === variantId || vote.variant_id_loser === variantId);

            if (shouldCount) {
                if (
                    uniqueCategories.user.every(user => user.user !== vote.user_name || user.isActive) &&
                    uniqueCategories.device.every(device => device.device !== vote.device || device.isActive) &&
                    uniqueCategories.resolution.every(resolution => resolution.resolution !== vote.screen_resolution || resolution.isActive) &&
                    isDateInRange(vote.vote_timestamp.slice(0, 10))
                ) {
                    // Count each vote participation as 1 (regardless of win/loss/tie)
                    count += 1;
                }
            }
            return count;
        }, 0); // Initialize count to 0
    };

    // Function to check if a date is within the selected range
    const isDateInRange = (timestamp) => {
        if (!uniqueCategories.startDate || !uniqueCategories.endDate) return true; // If no date range is selected, consider all dates
        return timestamp >= uniqueCategories.startDate && timestamp <= uniqueCategories.endDate;
    };

    // Get filtered votes based on current filters
    const getFilteredVotes = () => {
        if (!votes || !Array.isArray(votes)) return [];
        return votes.filter(voteMatchesFilter);
    };

    const getReferenceCandidateVariants = () => {
        if (!comparisonVariants.length) return [];
        const activeVariantIds = new Set(
            (uniqueCategories.variant || [])
                .filter((variant) => variant.isActive)
                .map((variant) => String(variant.variant))
        );
        return comparisonVariants.filter((variant) =>
            activeVariantIds.has(String(variant.variant_id))
        );
    };

    // Function to generate direct link to results with applied filter
    const generateFilteredLink = (updatedFilter) => {
        const newURL = {}
        for (const key in updatedFilter) {
            newURL[key] = ''
            if ((key === 'startDate') || key === 'endDate') {
                newURL[key] += updatedFilter[key] !== '' ? updatedFilter[key] : ''
            }
            else {
                updatedFilter[key].forEach((category) => {
                    if (!category.isActive) {
                        newURL[key] += `${category[key]}-`
                    }
                })
            }
        }
        window.history.pushState(null, '', `/results/${surveyId}?${objToQueryString(newURL)}`);
    };


    // Function to handle changes in filter options
    const handleFilterChange = (category, value) => {
        // Toggle value in the array for the selected category
        const updatedFilter = {
            ...uniqueCategories,
            [category]: uniqueCategories[category].map(categoryItem => {
                if (categoryItem[category] === value) {
                    return {
                        ...categoryItem,
                        isActive: !categoryItem.isActive // Toggle the isActive property
                    };
                } else {
                    return categoryItem;
                }
            })
        };

        setUniqueCategories(updatedFilter);
        generateFilteredLink(updatedFilter);
    };

    // Function to toggle all checkboxes for a given category
    const toggleAllCheckboxes = (category) => {
        // Toggle all checkboxes based on the current state
        const allChecked = uniqueCategories[category].every(item => item.isActive);
        const updatedFilter = {...uniqueCategories, [category]:
            uniqueCategories[category].map((category)=> {
                if (!allChecked) {
                    // Not all checkboxes are currently checked, check all
                    return {...category, isActive: true}
                } else {
                    // All checkboxes are currently checked, uncheck all
                    return {...category, isActive: false}
                }
            })
        }
        setUniqueCategories(updatedFilter);
        generateFilteredLink(updatedFilter)
    };

    const handleFilterDate = (type, value) => {
        const updatedFilter = {...uniqueCategories, [type]: value};
        setUniqueCategories(updatedFilter);
        generateFilteredLink(updatedFilter)}

    // Function to export all votes data to CSV
    const exportToCSV = () => {
        // Add BOM for UTF-8
        let csvData = '\uFEFF';

        // Construct CSV header
        csvData += "Vote Date,Vote ID,Vote Type,Variant 1 ID,Variant 2 ID,Variant 1 Name,Variant 2 Name,Winner ID,Loser ID,User ID,User Name,Media,Device,Screen Resolution\n";

        // Append each vote to CSV data
        votes.forEach(vote => {
            const voteType = vote.vote_type || 'winner_loser'; // Default to winner_loser for backwards compatibility

            // Determine winner/loser based on vote type
            let winnerId = '';
            let loserId = '';

            if (voteType === 'winner_loser') {
                winnerId = vote.variant_id_winner;
                loserId = vote.variant_id_loser;
            } else if (voteType === 'tie') {
                // For ties, both are winners (no loser)
                winnerId = 'TIE';
                loserId = 'TIE';
            } else if (voteType === 'equally_bad') {
                // For equally bad, both are losers (no winner)
                winnerId = 'EQUALLY_BAD';
                loserId = 'EQUALLY_BAD';
            }

            csvData += `${new Date(vote.vote_timestamp).toLocaleString().replace(',','')},${vote.vote_id},"${voteType}",${vote.variant_id_winner},${vote.variant_id_loser},"${vote.winner_variant_name}","${vote.loser_variant_name}",${winnerId},${loserId},${vote.user_id},"${vote.user_name}","${vote.media}",${vote.device},${vote.screen_resolution}\n`;
        });

        // Create Blob object
        const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });

        // Create URL for Blob object
        const url = URL.createObjectURL(blob);

        // Create link element
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `votes_data_for_${surveyId}_survey.csv`);

        // Simulate click on the link to initiate download
        document.body.appendChild(link);
        link.click();

        // Clean up
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // Function to check if a vote meets the filter criteria
    const voteMatchesFilter = (vote) => {
        // Check if both variants in the vote are active
        const winnerVariantActive = uniqueCategories.variant.some(v =>
            v.variant === vote.variant_id_winner && v.isActive
        );
        const loserVariantActive = uniqueCategories.variant.some(v =>
            v.variant === vote.variant_id_loser && v.isActive
        );

        return (
            winnerVariantActive &&
            loserVariantActive &&
            uniqueCategories.user.every(user => user.user !== vote.user_name || user.isActive) &&
            uniqueCategories.device.every(device => device.device !== vote.device || device.isActive) &&
            uniqueCategories.resolution.every(resolution => resolution.resolution !== vote.screen_resolution || resolution.isActive) &&
            isDateInRange(vote.vote_timestamp.slice(0, 10))
        );
    };

    // Function to calculate weighted results from filtered votes
    const calculateFilteredWeightedResults = () => {
        // If no votes or filters not initialized, return
        if (!votes.length || !uniqueCategories.user) {
            return;
        }

        // Apply filters to votes
        const filteredVotes = votes.filter(voteMatchesFilter);

        // If no filtered votes, return empty result
        if (filteredVotes.length === 0) {
            setFilteredWeightedResults({
                win_percentage_matrix: {},
                winning_averages: {},
                significance_matrix: {}
            });
            return;
        }

        // Get unique variants from filtered votes
        const uniqueVariants = [...new Set([
            ...filteredVotes.map(vote => vote.winner_variant_name),
            ...filteredVotes.map(vote => vote.loser_variant_name).filter(Boolean)
        ])];

        // Initialize matrices
        const winCounts = {};
        const matchCounts = {};
        const rawWinCounts = {}; // For p-value calculations
        const rawMatchCounts = {}; // For p-value calculations
        const significanceMatrix = {}; // To store p-values

        uniqueVariants.forEach(v1 => {
            winCounts[v1] = {};
            matchCounts[v1] = {};
            rawWinCounts[v1] = {};
            rawMatchCounts[v1] = {};
            significanceMatrix[v1] = {};

            uniqueVariants.forEach(v2 => {
                winCounts[v1][v2] = 0;
                matchCounts[v1][v2] = 0;
                rawWinCounts[v1][v2] = 0;
                rawMatchCounts[v1][v2] = 0;
                significanceMatrix[v1][v2] = 1; // Initialize p-value as 1 (not significant)
            });
        });

        // Group votes by user and calculate weights
        const userVotes = {};
        filteredVotes.forEach(vote => {
            if (!userVotes[vote.user_name]) {
                userVotes[vote.user_name] = [];
            }
            userVotes[vote.user_name].push(vote);
        });

        // Filter out users with less than 15 votes for weighted calculations
        const MIN_VOTES_FOR_WEIGHTED = 15;
        const qualifiedUserVotes = Object.entries(userVotes)
            .filter(([userName, userVoteList]) => userVoteList.length >= MIN_VOTES_FOR_WEIGHTED);

        // Calculate weighted wins for qualified users and track raw counts for significance
        qualifiedUserVotes.forEach(([userName, userVoteList]) => {
            const userWeight = 1 / userVoteList.length;
            userVoteList.forEach(vote => {
                if (vote.winner_variant_name && vote.loser_variant_name) {
                    if (vote.vote_type === 'winner_loser') {
                        // Normal vote: winner beats loser
                        winCounts[vote.winner_variant_name][vote.loser_variant_name] += userWeight;
                        matchCounts[vote.winner_variant_name][vote.loser_variant_name] += userWeight;
                        matchCounts[vote.loser_variant_name][vote.winner_variant_name] += userWeight;

                        // For significance calculations - raw counts
                        rawWinCounts[vote.winner_variant_name][vote.loser_variant_name] += 1;
                        rawMatchCounts[vote.winner_variant_name][vote.loser_variant_name] += 1;
                        rawMatchCounts[vote.loser_variant_name][vote.winner_variant_name] += 1;
                    } else if (vote.vote_type === 'tie') {
                        // Tie vote: both variants get 0.5 wins against each other
                        winCounts[vote.winner_variant_name][vote.loser_variant_name] += userWeight * 0.5;
                        winCounts[vote.loser_variant_name][vote.winner_variant_name] += userWeight * 0.5;
                        matchCounts[vote.winner_variant_name][vote.loser_variant_name] += userWeight;
                        matchCounts[vote.loser_variant_name][vote.winner_variant_name] += userWeight;

                        // For significance calculations - raw counts (count as 0.5 each)
                        rawWinCounts[vote.winner_variant_name][vote.loser_variant_name] += 0.5;
                        rawWinCounts[vote.loser_variant_name][vote.winner_variant_name] += 0.5;
                        rawMatchCounts[vote.winner_variant_name][vote.loser_variant_name] += 1;
                        rawMatchCounts[vote.loser_variant_name][vote.winner_variant_name] += 1;
                    } else if (vote.vote_type === 'equally_bad') {
                        // Equally bad vote: both variants get 0 wins (both are losers)
                        // We still count this as a match for percentage calculations
                        matchCounts[vote.winner_variant_name][vote.loser_variant_name] += userWeight;
                        matchCounts[vote.loser_variant_name][vote.winner_variant_name] += userWeight;

                        // For significance calculations - raw counts (no wins for either)
                        rawMatchCounts[vote.winner_variant_name][vote.loser_variant_name] += 1;
                        rawMatchCounts[vote.loser_variant_name][vote.winner_variant_name] += 1;
                    }
                }
            });
        });

        // Calculate win percentages
        const winPercentageMatrix = {};
        uniqueVariants.forEach(v1 => {
            winPercentageMatrix[v1] = {};
            uniqueVariants.forEach(v2 => {
                if (v1 === v2) {
                    winPercentageMatrix[v1][v2] = 0;
                } else if (matchCounts[v1][v2] > 0) {
                    winPercentageMatrix[v1][v2] = (winCounts[v1][v2] / matchCounts[v1][v2]) * 100;
                } else {
                    winPercentageMatrix[v1][v2] = 0;
                }
            });
        });

        // Calculate winning averages
        const winningAverages = {};
        uniqueVariants.forEach(variant => {
            const winPercentages = Object.entries(winPercentageMatrix[variant])
                .filter(([otherVariant]) => otherVariant !== variant)
                .map(([_, percentage]) => percentage);

            if (winPercentages.length > 0) {
                winningAverages[variant] = winPercentages.reduce((sum, val) => sum + val, 0) / winPercentages.length;
            } else {
                winningAverages[variant] = 0;
            }
        });

        // Calculate significance (p-values) using a binomial test
        uniqueVariants.forEach(v1 => {
            uniqueVariants.forEach(v2 => {
                if (v1 !== v2) {
                    const totalMatches = rawMatchCounts[v1][v2];
                    if (totalMatches > 0) {
                        const winsV1 = rawWinCounts[v1][v2];
                        // Binomial test - probability of observing at least winsV1 successes
                        // in totalMatches trials with 0.5 probability (null hypothesis)
                        try {
                            // Calculate p-value for two-tailed test
                            const pValue = calculateBinomialPValue(winsV1, totalMatches);
                            significanceMatrix[v1][v2] = pValue;
                        } catch (error) {
                            console.error("Error calculating p-value:", error);
                            significanceMatrix[v1][v2] = 1;
                        }
                    }
                }
            });
        });

        setFilteredWeightedResults({
            win_percentage_matrix: winPercentageMatrix,
            winning_averages: winningAverages,
            significance_matrix: significanceMatrix
        });
    };

    // Calculate p-value for binomial test
    const calculateBinomialPValue = (successes, trials) => {
        // Using two-tailed test with null hypothesis p=0.5
        const p = 0.5;

        if (trials === 0) return 1;
        if (successes > trials) return 1;

        // If we have jStat library
        if (typeof jStat !== 'undefined') {
            try {
                // For two-tailed test, we use the CDF to get the probability of observing
                // a result at least as extreme as the observed value
                let pValue;
                const observed = successes / trials;

                if (observed <= 0.5) {
                    // Left tail
                    pValue = 2 * jStat.binomial.cdf(successes, trials, p);
                } else {
                    // Right tail
                    pValue = 2 * (1 - jStat.binomial.cdf(successes - 1, trials, p));
                }

                return pValue;
            } catch (e) {
                console.error("Error with jStat calculation:", e);
            }
        }

        // Fallback calculation (simplified approximation)
        // This is a simpler approximation using normal approximation to binomial
        const mean = trials * p;
        const stdDev = Math.sqrt(trials * p * (1 - p));
        const zScore = Math.abs((successes - mean) / stdDev);
        // Approximate p-value from z-score
        return 2 * (1 - normalCDF(zScore, 0, 1));
    };

    // Normal cumulative distribution function (for fallback calculation)
    const normalCDF = (x, mean = 0, stdDev = 1) => {
        // Approximation to the normal CDF
        const t = 1 / (1 + 0.2316419 * Math.abs(x));
        const d = 0.3989423 * Math.exp(-x * x / 2);
        let prob = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
        if (x > 0) {
            prob = 1 - prob;
        }
        return prob;
    };

    // Function to get significance indicators based on p-value
    const getSignificanceIndicator = (pValue) => {
        if (pValue === null || pValue === undefined || isNaN(pValue)) return '';
        if (pValue < 0.001) return '***';
        if (pValue < 0.01) return '**';
        if (pValue < 0.05) return '*';
        return '';
    };

    // Calculate filtered weighted results whenever filters change
    useEffect(() => {
        if (votes && votes.length > 0 && uniqueCategories.user && uniqueCategories.variant) {
            calculateFilteredWeightedResults();
        }
    }, [uniqueCategories, votes]);



                // Smart Table Wrapper Component - Dynamic Height
    const SmartTable = ({ title, children, tableId }) => {
        const [fullscreen, setFullscreen] = useState(false);

        useEffect(() => {
            const handleEsc = (e) => {
                if (e.key === 'Escape' && fullscreen) {
                    setFullscreen(false);
                }
            };

            if (fullscreen) {
                document.addEventListener('keydown', handleEsc);
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = 'auto';
            }

            return () => {
                document.removeEventListener('keydown', handleEsc);
                document.body.style.overflow = 'auto';
            };
        }, [fullscreen]);

        const tableContent = (
            <div
                className="smart-table-wrapper table-view-normal"
                style={{
                    maxHeight: fullscreen ? 'calc(100vh - 120px)' : '70vh',
                    overflow: 'auto',
                    position: 'relative'
                }}
            >
                {children}
            </div>
        );

        return (
            <>
                {fullscreen && (
                    <div className="fullscreen-table-overlay">
                        <div className="fullscreen-header">
                            <div className="table-title">{title}</div>
                            <button
                                className="fullscreen-close-btn"
                                onClick={() => setFullscreen(false)}
                            >
                                ✕ Close Fullscreen
                            </button>
                        </div>
                        <div className="fullscreen-table-container">
                            {tableContent}
                        </div>
                    </div>
                )}

                <div className="smart-table-container">
                    <div className="table-controls">
                        <div className="table-title">{title}</div>
                        <div className="table-action-buttons">
                            {/* Fullscreen Button Only */}
                            <button
                                className="fullscreen-btn"
                                onClick={() => setFullscreen(true)}
                            >
                                <svg width="14" height="14" viewBox="0 0 16 16">
                                    <path d="M1.5 1h4v2h-2v2h-2V1zm9 0h4v4h-2V3h-2V1zM3.5 11v2h2v2h-4v-4zm9 0h2v4h-4v-2h2v-2z"/>
                                </svg>
                                Fullscreen
                            </button>
                        </div>
                    </div>
                    {tableContent}
                </div>
            </>
        );
    };

    // Render the matrix table
    const renderWeightedMatrix = () => {
        // Use filtered results if available AND has data, otherwise use original weighted results
        const resultsToUse = (filteredWeightedResults && Object.keys(filteredWeightedResults.win_percentage_matrix || {}).length > 0)
            ? filteredWeightedResults
            : weightedResults;

        if (!resultsToUse || !resultsToUse.win_percentage_matrix || Object.keys(resultsToUse.win_percentage_matrix).length === 0) {
            return <p>No weighted data available</p>;
        }

        // Get active variant names
        const activeVariantNames = uniqueCategories.variant
            .filter(v => v.isActive)
            .map(v => v.variantName);

        const variants = Object.keys(resultsToUse.win_percentage_matrix)
            .filter(variant => activeVariantNames.includes(variant));

        if (variants.length === 0) {
            return <p>No weighted data available for selected variants</p>;
        }

        // Smooth gradient color function for heatmap - Red to Yellow to Green
        const getHeatmapColorRedGreen = (percentage) => {
            // Normalize percentage to 0-1 range
            const normalized = percentage / 100;

            // Create smooth gradient from red (low) to yellow (mid) to green (high)
            let r, g, b;

            if (normalized < 0.5) {
                // Red to Yellow transition (0% to 50%)
                const factor = normalized * 2;
                r = 220; // Deep red
                g = Math.floor(220 * factor); // Gradually increase green
                b = 38;
            } else {
                // Yellow to Green transition (50% to 100%)
                const factor = (normalized - 0.5) * 2;
                r = Math.floor(220 * (1 - factor)); // Gradually decrease red
                g = 220; // Keep green high
                b = 38;
            }

            // Calculate luminance for text color decision
            const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
            const textColor = luminance > 0.5 ? '#1f2937' : '#ffffff';

            // Add opacity for softer appearance
            const bgColor = `rgba(${r}, ${g}, ${b}, 0.85)`;

            return { bg: bgColor, text: textColor, raw: { r, g, b } };
        };

        // Alternative gradient function - Blue to Red through White
        const getHeatmapColorBlueRed = (percentage) => {
            const normalized = percentage / 100;
            let r, g, b;

            if (normalized < 0.5) {
                // Blue to White (0% to 50%)
                const factor = normalized * 2;
                r = Math.floor(59 + (255 - 59) * factor);
                g = Math.floor(130 + (255 - 130) * factor);
                b = Math.floor(246 + (255 - 246) * factor);
            } else {
                // White to Red (50% to 100%)
                const factor = (normalized - 0.5) * 2;
                r = Math.floor(255 - (255 - 239) * factor);
                g = Math.floor(255 - (255 - 68) * factor);
                b = Math.floor(255 - (255 - 68) * factor);
            }

            const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
            const textColor = luminance > 0.6 ? '#1f2937' : '#ffffff';
            const bgColor = `rgb(${r}, ${g}, ${b})`;

            return { bg: bgColor, text: textColor };
        };

        // Cool gradient function - Purple to Orange through Teal (Viridis-like)
        const getHeatmapColorViridis = (percentage) => {
            const normalized = percentage / 100;
            let r, g, b;

            if (normalized < 0.25) {
                // Deep Purple to Blue (0% to 25%)
                const factor = normalized * 4;
                r = Math.floor(68 + (33 - 68) * factor);
                g = Math.floor(1 + (150 - 1) * factor);
                b = Math.floor(84 + (243 - 84) * factor);
            } else if (normalized < 0.5) {
                // Blue to Teal (25% to 50%)
                const factor = (normalized - 0.25) * 4;
                r = Math.floor(33 + (32 - 33) * factor);
                g = Math.floor(150 + (201 - 150) * factor);
                b = Math.floor(243 + (184 - 243) * factor);
            } else if (normalized < 0.75) {
                // Teal to Yellow-Green (50% to 75%)
                const factor = (normalized - 0.5) * 4;
                r = Math.floor(32 + (146 - 32) * factor);
                g = Math.floor(201 + (219 - 201) * factor);
                b = Math.floor(184 + (85 - 184) * factor);
            } else {
                // Yellow-Green to Yellow (75% to 100%)
                const factor = (normalized - 0.75) * 4;
                r = Math.floor(146 + (253 - 146) * factor);
                g = Math.floor(219 + (231 - 219) * factor);
                b = Math.floor(85 + (36 - 85) * factor);
            }

            const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
            const textColor = luminance > 0.6 ? '#1f2937' : '#ffffff';
            const bgColor = `rgb(${r}, ${g}, ${b})`;

            return { bg: bgColor, text: textColor };
        };

        // Monochrome Blue gradient
        const getHeatmapColorBlue = (percentage) => {
            const normalized = percentage / 100;
            // Light blue to dark blue
            const r = Math.floor(224 - (224 - 30) * normalized);
            const g = Math.floor(242 - (242 - 58) * normalized);
            const b = Math.floor(255 - (255 - 138) * normalized);

            const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
            const textColor = luminance > 0.5 ? '#1f2937' : '#ffffff';
            const bgColor = `rgb(${r}, ${g}, ${b})`;

            return { bg: bgColor, text: textColor };
        };

        // Purple to Gold gradient (elegant)
        const getHeatmapColorPurpleGold = (percentage) => {
            const normalized = percentage / 100;
            let r, g, b;

            if (normalized < 0.5) {
                // Purple to Pink
                const factor = normalized * 2;
                r = Math.floor(88 + (219 - 88) * factor);
                g = Math.floor(28 + (39 - 28) * factor);
                b = Math.floor(135 + (146 - 135) * factor);
            } else {
                // Pink to Gold
                const factor = (normalized - 0.5) * 2;
                r = Math.floor(219 + (255 - 219) * factor);
                g = Math.floor(39 + (215 - 39) * factor);
                b = Math.floor(146 - (146 - 0) * factor);
            }

            const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
            const textColor = luminance > 0.5 ? '#1f2937' : '#ffffff';
            const bgColor = `rgb(${r}, ${g}, ${b})`;

            return { bg: bgColor, text: textColor };
        };

        // Select color function based on state
        const getColorForCell = {
            'viridis': getHeatmapColorViridis,
            'redgreen': getHeatmapColorRedGreen,
            'bluered': getHeatmapColorBlueRed,
            'blue': getHeatmapColorBlue,
            'purplegold': getHeatmapColorPurpleGold
        }[heatmapColorScheme] || getHeatmapColorViridis;

        // Create color scale legend with scheme selector
        const ColorScaleLegend = () => (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                marginBottom: '20px',
                padding: '12px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '8px',
                backdropFilter: 'blur(10px)'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                }}>
                    <span style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#94a3b8',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}>
                        Win Rate
                    </span>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        <span style={{ fontSize: '11px', color: '#e2e8f0' }}>0%</span>
                        <div style={{
                            width: '200px',
                            height: '12px',
                            borderRadius: '6px',
                            background: `linear-gradient(to right,
                                ${getColorForCell(0).bg},
                                ${getColorForCell(25).bg},
                                ${getColorForCell(50).bg},
                                ${getColorForCell(75).bg},
                                ${getColorForCell(100).bg})`,
                            boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.2)'
                        }} />
                        <span style={{ fontSize: '11px', color: '#e2e8f0' }}>100%</span>
                    </div>
                </div>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                }}>
                    <label style={{
                        fontSize: '11px',
                        color: '#94a3b8',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        fontWeight: '600'
                    }}>
                        Color Scheme:
                    </label>
                    <select
                        value={heatmapColorScheme}
                        onChange={(e) => setHeatmapColorScheme(e.target.value)}
                        style={{
                            padding: '4px 8px',
                            fontSize: '11px',
                            borderRadius: '4px',
                            background: 'rgba(30, 41, 59, 0.8)',
                            color: '#e2e8f0',
                            border: '1px solid rgba(148, 163, 184, 0.3)',
                            cursor: 'pointer',
                            outline: 'none'
                        }}
                    >
                        <option value="viridis">Viridis</option>
                        <option value="redgreen">Red-Green</option>
                        <option value="bluered">Blue-Red</option>
                        <option value="blue">Blue Scale</option>
                        <option value="purplegold">Purple-Gold</option>
                    </select>
                </div>
            </div>
        );

        return (
            <div style={{ position: 'relative' }}>
                <ColorScaleLegend />
                <div style={{
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    borderRadius: '12px',
                    background: 'rgba(30, 41, 59, 0.5)',
                    padding: '16px',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
                }}>
                    <table style={{
                        borderSpacing: '3px',
                        borderCollapse: 'separate',
                        width: '100%',
                        tableLayout: 'fixed'
                    }}>
                        <thead>
                            <tr>
                                <th style={{
                                    background: 'linear-gradient(135deg, #1e293b 0%, #475569 100%)',
                                    color: '#e2e8f0',
                                    padding: '14px',
                                    textAlign: 'center',
                                    fontWeight: '700',
                                    fontSize: '11px',
                                    letterSpacing: '1px',
                                    textTransform: 'uppercase',
                                    borderRadius: '8px',
                                    minWidth: '160px',
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 11,
                                    boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                        <span>Variant</span>
                                        <span style={{
                                            fontSize: '14px',
                                            filter: 'grayscale(0.3)'
                                        }}>🎯</span>
                                    </div>
                                </th>
                                {variants.map((variant, idx) => (
                                    <th key={variant} style={{
                                        background: 'linear-gradient(135deg, #312e81 0%, #6366f1 100%)',
                                        color: '#f0f9ff',
                                        padding: '12px 6px',
                                        textAlign: 'center',
                                        minWidth: '60px',
                                        maxWidth: '80px',
                                        fontSize: '12px',
                                        fontWeight: '700',
                                        letterSpacing: '0.5px',
                                        borderRadius: '8px',
                                        height: '50px',
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 10,
                                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                        whiteSpace: 'nowrap'
                                    }}
                                    title={variant} // Add tooltip with full variant name
                                    >
                                        <div style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '2px'
                                        }}>
                                            <span style={{ fontSize: '10px', opacity: 0.8 }}>vs.</span>
                                            <span style={{ fontSize: '16px', fontWeight: '800' }}>{idx + 1}</span>
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {variants.map((rowVariant, rowIdx) => (
                                <tr key={rowVariant}>
                                    <td style={{
                                        background: 'linear-gradient(135deg, #312e81 0%, #6366f1 100%)',
                                        color: '#f0f9ff',
                                        padding: '12px',
                                        fontWeight: '600',
                                        fontSize: '12px',
                                        letterSpacing: '0.3px',
                                        borderRadius: '8px',
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 9,
                                        minWidth: '160px',
                                        boxShadow: '2px 0 8px rgba(0, 0, 0, 0.1)'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}>
                                            <span style={{
                                                background: 'rgba(255, 255, 255, 0.2)',
                                                borderRadius: '4px',
                                                padding: '2px 6px',
                                                fontSize: '11px',
                                                fontWeight: '700',
                                                minWidth: '20px',
                                                textAlign: 'center'
                                            }}>
                                                {rowIdx + 1}
                                            </span>
                                            <div style={{
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                flex: 1
                                            }} title={rowVariant}>
                                                {rowVariant}
                                            </div>
                                        </div>
                                    </td>
                                    {variants.map((colVariant, colIdx) => {
                                        if (rowVariant === colVariant) {
                                            // Diagonal cell (self vs self)
                                            return (
                                                <td key={colVariant} style={{
                                                    background: `repeating-linear-gradient(
                                                        45deg,
                                                        rgba(148, 163, 184, 0.1),
                                                        rgba(148, 163, 184, 0.1) 3px,
                                                        rgba(148, 163, 184, 0.05) 3px,
                                                        rgba(148, 163, 184, 0.05) 6px
                                                    )`,
                                                    textAlign: 'center',
                                                    padding: '12px',
                                                    minWidth: '60px',
                                                    maxWidth: '80px',
                                                    height: '50px',
                                                    borderRadius: '6px',
                                                    border: '1px solid rgba(148, 163, 184, 0.2)'
                                                }}>
                                                    <span style={{
                                                        fontSize: '18px',
                                                        color: '#64748b',
                                                        fontWeight: '300',
                                                        opacity: 0.5
                                                    }}>—</span>
                                                </td>
                                            );
                                        }

                                        const winRate = resultsToUse.win_percentage_matrix[rowVariant][colVariant];
                                        const percentage = winRate.toFixed(1);

                                        // Add significance stars if available
                                        let stars = '';
                                        let isSignificant = false;
                                        if (resultsToUse.significance_matrix &&
                                            resultsToUse.significance_matrix[rowVariant] &&
                                            resultsToUse.significance_matrix[rowVariant][colVariant]) {
                                            const pValue = resultsToUse.significance_matrix[rowVariant][colVariant];
                                            stars = getSignificanceIndicator(pValue);
                                            isSignificant = pValue < 0.05;
                                        }

                                        const cellColor = getColorForCell(winRate);

                                        return (
                                            <td key={colVariant} style={{
                                                background: cellColor.bg,
                                                textAlign: 'center',
                                                padding: '12px',
                                                minWidth: '60px',
                                                maxWidth: '80px',
                                                height: '50px',
                                                position: 'relative',
                                                borderRadius: '6px',
                                                border: `1px solid ${isSignificant ? 'rgba(251, 191, 36, 0.4)' : 'transparent'}`,
                                                boxShadow: isSignificant
                                                    ? '0 0 0 2px rgba(251, 191, 36, 0.2)'
                                                    : '0 1px 3px rgba(0, 0, 0, 0.1)',
                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                cursor: 'pointer',
                                                transform: 'scale(1)',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'scale(1.05)';
                                                e.currentTarget.style.boxShadow = isSignificant
                                                    ? '0 0 0 3px rgba(251, 191, 36, 0.3), 0 4px 12px rgba(0, 0, 0, 0.15)'
                                                    : '0 4px 12px rgba(0, 0, 0, 0.15)';
                                                e.currentTarget.style.zIndex = '20';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'scale(1)';
                                                e.currentTarget.style.boxShadow = isSignificant
                                                    ? '0 0 0 2px rgba(251, 191, 36, 0.2)'
                                                    : '0 1px 3px rgba(0, 0, 0, 0.1)';
                                                e.currentTarget.style.zIndex = 'auto';
                                            }}
                                            title={`${rowIdx + 1}. ${rowVariant} vs ${colIdx + 1}. ${colVariant}: ${percentage}% win rate${stars ? ` (${stars})` : ''}`}
                                            >
                                                <div style={{
                                                    fontWeight: isSignificant ? '700' : '600',
                                                    fontSize: '14px',
                                                    color: cellColor.text,
                                                    textShadow: isSignificant ? '0 1px 2px rgba(0, 0, 0, 0.1)' : 'none'
                                                }}>
                                                    {percentage}%
                                                    {stars && (
                                                        <sup style={{
                                                            color: '#fbbf24',
                                                            fontSize: '10px',
                                                            marginLeft: '2px',
                                                            fontWeight: '700',
                                                            textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
                                                        }}>
                                                            {stars}
                                                        </sup>
                                                    )}
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Variant Legend */}
                <div style={{
                    marginTop: '16px',
                    padding: '12px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '8px',
                    backdropFilter: 'blur(10px)'
                }}>
                    <h6 style={{
                        fontSize: '12px',
                        fontWeight: '600',
                        color: '#94a3b8',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        marginBottom: '8px'
                    }}>
                        Variant Reference
                    </h6>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                        gap: '8px',
                        fontSize: '12px'
                    }}>
                        {variants.map((variant, idx) => (
                            <div key={variant} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '4px 8px',
                                background: 'rgba(99, 102, 241, 0.1)',
                                borderRadius: '4px',
                                color: '#e2e8f0'
                            }}>
                                <span style={{
                                    background: 'rgba(99, 102, 241, 0.3)',
                                    borderRadius: '3px',
                                    padding: '2px 6px',
                                    fontSize: '11px',
                                    fontWeight: '700',
                                    minWidth: '24px',
                                    textAlign: 'center',
                                    color: '#f0f9ff'
                                }}>
                                    {idx + 1}
                                </span>
                                <span style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }} title={variant}>
                                    {variant}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Significance Legend */}
                <div style={{
                    marginTop: '12px',
                    padding: '8px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '6px',
                    fontSize: '11px',
                    color: '#94a3b8',
                    textAlign: 'center'
                }}>
                    <span style={{ marginRight: '16px' }}>
                        ⭐ p {"<"} 0.05 (significant)
                    </span>
                    <span style={{ marginRight: '16px' }}>
                        ⭐⭐ p {"<"} 0.01 (highly significant)
                    </span>
                    <span>
                        ⭐⭐⭐ p {"<"} 0.001 (very highly significant)
                    </span>
                </div>
            </div>
        );
    };

    // Render winning averages
    const renderWinningAverages = () => {
        // Use filtered results if available AND has data, otherwise use original weighted results
        const resultsToUse = (filteredWeightedResults && Object.keys(filteredWeightedResults.winning_averages || {}).length > 0)
            ? filteredWeightedResults
            : weightedResults;

        if (!resultsToUse || !resultsToUse.winning_averages || Object.keys(resultsToUse.winning_averages).length === 0) {
            return <p>No weighted data available</p>;
        }

        // Get active variant names
        const activeVariantNames = uniqueCategories.variant
            .filter(v => v.isActive)
            .map(v => v.variantName);

        const sortedVariants = Object.entries(resultsToUse.winning_averages)
            .filter(([variant]) => activeVariantNames.includes(variant))
            .sort((a, b) => b[1] - a[1]);

        if (sortedVariants.length === 0) {
            return <p>No weighted data available for selected variants</p>;
        }

        return (
            <Table hover className="mb-0">
                <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <tr>
                        <th>Variant</th>
                        <th className="text-center">Weighted Win Rate (%)</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedVariants.map(([variant, percentage]) => (
                        <tr key={variant}>
                            <td style={{ verticalAlign: 'middle' }}>
                                <strong className="variant-name-full" title={variant}>{variant}</strong>
                            </td>
                                                        <td className="text-center">
                                <Badge
                                    bg="secondary"
                                    style={{
                                        background: (() => {
                                            if (percentage >= 55) {
                                                // Winner - mint/teal (matching Vote Distribution Win %)
                                                return 'linear-gradient(135deg, hsl(166, 69%, 81%) 0%, hsl(166, 69%, 71%) 100%)';
                                            } else if (percentage >= 45) {
                                                // Neutral - light gray
                                                return 'linear-gradient(135deg, hsl(230, 15%, 75%) 0%, hsl(230, 15%, 65%) 100%)';
                                            } else {
                                                // Underperformer - slightly darker gray
                                                return 'linear-gradient(135deg, hsl(230, 15%, 65%) 0%, hsl(230, 15%, 55%) 100%)';
                                            }
                                        })(),
                                        color: percentage >= 45 ? 'hsl(230, 15%, 15%)' : 'white',
                                        fontSize: '0.95rem',
                                        padding: '0.45em 1em',
                                        fontWeight: '600',
                                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                        borderRadius: 'var(--radius-sm, 0.375rem)',
                                        border: 'none'
                                    }}
                                >
                                    {percentage.toFixed(1)}%
                                </Badge>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        );
    };

    // Function to count votes for a specific user
    const countVotesForUser = (votes, userName) => {
        // Check if votes is an array before trying to filter
        if (!Array.isArray(votes)) {
            return 0;
        }
        return votes.filter(vote => vote.user_name === userName).length;
    };

    // State for responsive layout
    const [screenSize, setScreenSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
        isWideScreen: window.innerWidth >= 1400,
        isUltraWide: window.innerWidth >= 1920,
        is4K: window.innerWidth >= 2560
    });

    // State for ELO auto-update preference
    const [autoUpdateElo, setAutoUpdateElo] = useState(false);

    // Update screen size on window resize
    useEffect(() => {
        const handleResize = () => {
            setScreenSize({
                width: window.innerWidth,
                height: window.innerHeight,
                isWideScreen: window.innerWidth >= 1400,
                isUltraWide: window.innerWidth >= 1920,
                is4K: window.innerWidth >= 2560
            });
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <Container fluid className="results-container">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className="results-title mb-0">Survey Results</h2>
                                <Button
                    className="btn-export"
                    onClick={exportToCSV}
                    style={{
                        background: 'hsla(166, 69%, 81%, 0.2)',
                        border: '1px solid hsla(166, 69%, 81%, 0.5)',
                        color: 'hsl(166, 69%, 81%)',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '4px' /* Changed to match --radius-sm */
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.background = 'hsl(166, 69%, 81%)';
                        e.target.style.color = 'hsl(230, 15%, 15%)';
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 4px 12px hsla(166, 69%, 81%, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.background = 'hsla(166, 69%, 81%, 0.2)';
                        e.target.style.color = 'hsl(166, 69%, 81%)';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = 'none';
                    }}
                >
                    📊 Export to CSV
                </Button>
            </div>
            {votes.length > 0 ?
                <>
                    <Row>
                        <Col xl={3} lg={4} md={4} className="filter-sidebar bg-secondary-subtle rounded p-3 mb-3">
                            <h5>Filters</h5>
                            <hr />
                            {/* Variant filter */}
                            <Form.Group className="mb-3">
                                <Form.Label className='fw-bold'>Filter by Variants:</Form.Label>
                                <Button
                                    variant={uniqueCategories.variant.every(item => item.isActive) ? "outline-primary" : "primary"}
                                    size="sm"
                                    className="mb-2 d-block"
                                    onClick={() => toggleAllCheckboxes('variant')}
                                >
                                    {uniqueCategories.variant.every(item => item.isActive) ? "Deselect All" : "Select All"}
                                </Button>
                                {uniqueCategories.variant.map((variant) => (
                                    <Form.Check
                                        key={variant.variant}
                                        type="checkbox"
                                        id={`variant-${variant.variant}`}
                                        label={variant.variantName}
                                        checked={variant.isActive}
                                        onChange={() => handleFilterChange('variant', variant.variant)}
                                    />
                                ))}
                            </Form.Group>
                            {/* User filter */}
                            <Form.Group className="mb-3">
                                <Form.Label className='fw-bold'>Filter by Users:</Form.Label>
                                <small className="text-warning d-block mb-2">
                                    ⚠️ Users with &lt;15 votes excluded from weighted analysis
                                </small>
                                <Button
                                    variant={uniqueCategories.user.every(item => item.isActive) ? "outline-primary" : "primary"}
                                    size="sm"
                                    className="mb-2 d-block"
                                    onClick={() => toggleAllCheckboxes('user')}
                                >
                                    {uniqueCategories.user.every(item => item.isActive) ? "Deselect All" : "Select All"}
                                </Button>
                                {[...uniqueCategories.user]
                                    .sort((a, b) => countVotesForUser(votes, b.user) - countVotesForUser(votes, a.user))
                                    .map(user => {
                                        const voteCount = countVotesForUser(votes, user.user);
                                        const isQualifiedForWeighted = voteCount >= 15;

                                        return (
                                            <Form.Check
                                                key={user.user}
                                                type="checkbox"
                                                id={`user-${user.user}`}
                                                label={
                                                    <span style={{
                                                        opacity: isQualifiedForWeighted ? 1 : 0.5,
                                                        fontStyle: isQualifiedForWeighted ? 'normal' : 'italic'
                                                    }}>
                                                        {user.user}
                                                        <Badge
                                                            bg={isQualifiedForWeighted ? "primary" : "secondary"}
                                                            className="ms-2"
                                                            style={{ fontSize: '0.75em' }}
                                                        >
                                                            {voteCount}
                                                        </Badge>
                                                        {!isQualifiedForWeighted && (
                                                            <OverlayTrigger
                                                                placement="right"
                                                                overlay={
                                                                    <Tooltip>
                                                                        Excluded from weighted analysis (needs 15+ votes)
                                                                    </Tooltip>
                                                                }
                                                            >
                                                                <span className="text-warning ms-1" style={{ fontSize: '0.85em', cursor: 'help' }}>
                                                                    ⚠️
                                                                </span>
                                                            </OverlayTrigger>
                                                        )}
                                                    </span>
                                                }
                                                checked={user.isActive}
                                                onChange={() => handleFilterChange('user', user.user)}
                                            />
                                        );
                                    })}
                            </Form.Group>
                            {/* Date filter */}
                            <Form.Group className="mb-3">
                                <Form.Label className='fw-bold'>Filter by Dates:</Form.Label>
                                <Form.Control
                                    type="date"
                                    placeholder="Start Date"
                                    min={findMinDate(votes.map(vote => vote.vote_timestamp))}
                                    value={uniqueCategories.startDate}
                                    onChange={(e) => handleFilterDate('startDate', e.target.value)}
                                />
                                <Form.Control
                                    type="date"
                                    placeholder="End Date"
                                    max={findMaxDate(votes.map(vote => vote.vote_timestamp))}
                                    value={uniqueCategories.endDate}
                                    onChange={(e) => handleFilterDate('endDate', e.target.value)}
                                />
                            </Form.Group>
                            {/* devices filter */}
                            <Form.Group className="mb-3">
                                <Form.Label className='fw-bold'>Filter by Devices:</Form.Label>
                                <Button
                                    variant={uniqueCategories.device.every(item => item.isActive) ? "outline-primary" : "primary"}
                                    size="sm"
                                    className="mb-2 d-block"
                                    onClick={() => toggleAllCheckboxes('device')}
                                >
                                    {uniqueCategories.device.every(item => item.isActive) ? "Deselect All" : "Select All"}
                                </Button>
                                {uniqueCategories.device.map(device => (
                                    <Form.Check
                                        key={device.device}
                                        type="checkbox"
                                        id={`device-${device.device}`}
                                        label={device.device}
                                        checked={device.isActive}
                                        onChange={() => handleFilterChange('device', device.device)}
                                    />
                                ))}
                            </Form.Group>
                            {/* screen resolutions filter */}
                            <Form.Group className="mb-3">
                                <Form.Label className='fw-bold'>Filter by Screen Resolutions:</Form.Label>
                                <Button
                                    variant={uniqueCategories.resolution.every(item => item.isActive) ? "outline-primary" : "primary"}
                                    size="sm"
                                    className="mb-2 d-block"
                                    onClick={() => toggleAllCheckboxes('resolution')}
                                >
                                    {uniqueCategories.resolution.every(item => item.isActive) ? "Deselect All" : "Select All"}
                                </Button>
                                {uniqueCategories.resolution.map(resolution => (
                                    <Form.Check
                                        key={resolution.resolution}
                                        type="checkbox"
                                        id={`resolution-${resolution.resolution}`}
                                        label={resolution.resolution}
                                        checked={resolution.isActive}
                                        onChange={() => handleFilterChange('resolution', resolution.resolution)}
                                    />
                                ))}
                            </Form.Group>
                        </Col>
                        <Col xl={9} lg={8} md={8}>
                            <Tab.Container activeKey={activeTab} onSelect={(key) => setActiveTab(key)}>
                                <Nav variant="tabs" className="mb-3">
                                    {comparisonScheme === 'reference_vs_all' && (
                                        <Nav.Item>
                                            <Nav.Link eventKey="reference">
                                                🎯 Reference Analysis
                                            </Nav.Link>
                                        </Nav.Item>
                                    )}
                                    <Nav.Item>
                                        <Nav.Link eventKey="analysis">
                                            📊 Analysis
                                            {isLoadingWeighted && <span className="ms-2 loading-inline-wrapper"><div className="spinner-border spinner-border-sm" role="status"><span className="visually-hidden">Loading...</span></div></span>}
                                        </Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey="elo">
                                            🏆 ELO Arena
                                        </Nav.Link>
                                    </Nav.Item>
                                </Nav>

                                                                <Tab.Content>
                                    {comparisonScheme === 'reference_vs_all' && (
                                        <Tab.Pane eventKey="reference">
                                            <ReferenceResults
                                                votes={getFilteredVotes()}
                                                referenceVariantId={
                                                    referenceVariantId ??
                                                    uniqueCategories.variant?.find(
                                                        (variant) => variant.variantName === referenceVariantName
                                                    )?.variant
                                                }
                                                referenceVariantName={referenceVariantName || 'Reference'}
                                                candidateVariants={getReferenceCandidateVariants()}
                                            />
                                        </Tab.Pane>
                                    )}
                                    <Tab.Pane eventKey="analysis">
                                        {isLoadingWeighted ? (
                                            <div className="text-center p-4">
                                                <Loading message="Calculating results" />
                                            </div>
                                        ) : (
                                            <div className="analysis-dashboard">
                                                {/* Top Summary Cards */}
                                                {(() => {
                                                    const filteredVotes = getFilteredVotes();
                                                    return (
                                                        <>
                                                            <div className="summary-cards-grid mb-4">
                                                                <Card className="summary-card shadow-sm">
                                                                    <Card.Body className="text-center p-3">
                                                                        <div className="summary-icon mb-2">📊</div>
                                                                        <h3 className="text-primary mb-0">
                                                                            {filteredVotes.length}
                                                                        </h3>
                                                                        <small className="text-muted">Total Votes</small>
                                                                    </Card.Body>
                                                                </Card>
                                                                <Card className="summary-card shadow-sm">
                                                                    <Card.Body className="text-center p-3">
                                                                        <div className="summary-icon mb-2">🎯</div>
                                                                        <h3 className="text-success mb-0">
                                                                            {uniqueCategories.variant.filter(v => v.isActive).length}
                                                                        </h3>
                                                                        <small className="text-muted">Active Variants</small>
                                                                    </Card.Body>
                                                                </Card>
                                                                <Card className="summary-card shadow-sm">
                                                                    <Card.Body className="text-center p-3">
                                                                        <div className="summary-icon mb-2">👥</div>
                                                                        <h3 className="text-info mb-0">
                                                                            {uniqueCategories.user.filter(u => u.isActive).length}
                                                                        </h3>
                                                                        <small className="text-muted">Active Users</small>
                                                                    </Card.Body>
                                                                </Card>
                                                                <Card className="summary-card shadow-sm">
                                                                    <Card.Body className="text-center p-3">
                                                                        <div className="summary-icon mb-2">🤝</div>
                                                                        <h3 className="text-warning mb-0">
                                                                            {filteredVotes.filter(v => v.vote_type === 'tie').length}
                                                                        </h3>
                                                                        <small className="text-muted">Tie Votes</small>
                                                                    </Card.Body>
                                                                </Card>
                                                                <Card className="summary-card shadow-sm">
                                                                    <Card.Body className="text-center p-3">
                                                                        <div className="summary-icon mb-2">👎</div>
                                                                        <h3 className="text-danger mb-0">
                                                                            {filteredVotes.filter(v => v.vote_type === 'equally_bad').length}
                                                                        </h3>
                                                                        <small className="text-muted">Equally Bad</small>
                                                                    </Card.Body>
                                                                </Card>
                                                            </div>

                                                {/* Main Results Section */}
                                                <div className="mb-4">
                                                    <SmartTable title="📈 Vote Distribution" tableId="vote-distribution">
                                                            <Table hover className="mb-0">
                                                                <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                                                                    <tr>
                                                                        <th>Variant</th>
                                                                        <th className="text-center">Raw Votes</th>
                                                                        <th className="text-center">Display %</th>
                                                                        <th className="text-center" title="Hover for W/L/T/EB breakdown">Win %</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                                                                                                {uniqueCategories.variant
                                                                            .filter(variant => variant.isActive)
                                                                            .map((variant) => {
                                                                                // Get active variant IDs for filtering
                                                                                const activeVariantIds = uniqueCategories.variant
                                                                                    .filter(v => v.isActive)
                                                                                    .map(v => v.variant);

                                                                                // Only count votes between active variants
                                                                                const relevantVotes = filteredVotes.filter(v =>
                                                                                    activeVariantIds.includes(v.variant_id_winner) &&
                                                                                    activeVariantIds.includes(v.variant_id_loser)
                                                                                );

                                                                                const voteCount = countVotesForVariant(relevantVotes, variant.variant);
                                                                                const totalVotes = uniqueCategories.variant
                                                                                    .filter(v => v.isActive)
                                                                                    .reduce((sum, v) => sum + countVotesForVariant(relevantVotes, v.variant), 0);
                                                                                const displayPercentage = totalVotes > 0 ? (voteCount / totalVotes * 100) : 0;

                                                                                // Calculate win percentage (only considering battles with other active variants)
                                                                                const wins = relevantVotes.filter(v =>
                                                                                    v.variant_id_winner === variant.variant && v.vote_type === 'winner_loser'
                                                                                ).length;
                                                                                const losses = relevantVotes.filter(v =>
                                                                                    v.variant_id_loser === variant.variant && v.vote_type === 'winner_loser'
                                                                                ).length;
                                                                                const ties = relevantVotes.filter(v =>
                                                                                    v.vote_type === 'tie' &&
                                                                                    (v.variant_id_winner === variant.variant || v.variant_id_loser === variant.variant)
                                                                                ).length;

                                                                                const equallyBad = relevantVotes.filter(v =>
                                                                                    v.vote_type === 'equally_bad' &&
                                                                                    (v.variant_id_winner === variant.variant || v.variant_id_loser === variant.variant)
                                                                                ).length;

                                                                                const totalBattles = wins + losses + ties + equallyBad;
                                                                                // In win percentage: wins = 1 point, ties = 0.5 points, losses and equally_bad = 0 points
                                                                                const winPercentage = totalBattles > 0 ? ((wins + ties * 0.5) / totalBattles * 100) : 0;

                                                                                return {
                                                                                    variant,
                                                                                    voteCount,
                                                                                    displayPercentage,
                                                                                    winPercentage,
                                                                                    wins,
                                                                                    losses,
                                                                                    ties,
                                                                                    equallyBad
                                                                                };
                                                                            })
                                                                            .sort((a, b) => b.winPercentage - a.winPercentage) // Sort by win percentage descending
                                                                            .map(({ variant, voteCount, displayPercentage, winPercentage, wins, losses, ties, equallyBad }) => (
                                                                                <tr key={variant.variant}>
                                                                                    <td>
                                                                                        <strong>{variant.variantName}</strong>
                                                                                    </td>
                                                                                    <td className="text-center">
                                                                                        <Badge bg="secondary">{voteCount}</Badge>
                                                                                    </td>
                                                                                    <td className="text-center">
                                                                                        {displayPercentage.toFixed(1)}%
                                                                                    </td>
                                                                                    <td className="text-center">
                                                                                        <OverlayTrigger
                                                                                            placement="top"
                                                                                            overlay={
                                                                                                <Tooltip>
                                                                                                    <div>
                                                                                                        <strong>W:</strong> {wins} |
                                                                                                        <strong> L:</strong> {losses} |
                                                                                                        <strong> T:</strong> {ties}
                                                                                                        {equallyBad > 0 && (
                                                                                                            <span> | <strong> EB:</strong> {equallyBad}</span>
                                                                                                        )}
                                                                                                    </div>
                                                                                                </Tooltip>
                                                                                            }
                                                                                        >
                                                                                            <Badge bg="secondary">
                                                                                                {winPercentage.toFixed(1)}%
                                                                                            </Badge>
                                                                                        </OverlayTrigger>
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                    </tbody>
                                                                </Table>
                                                        </SmartTable>
                                                </div>

                                                                                                <div className="mb-4">
                                                        <SmartTable
                                                            title="⚖️ Weighted Win Rates"
                                                            tableId="weighted-win-rates"
                                                        >
                                                            {renderWinningAverages()}
                                                        </SmartTable>
                                                </div>

                                                                                                {/* Detailed Matrix Section */}
                                                <div className="mb-4">
                                                    <SmartTable
                                                        title="🎯 Head-to-Head Comparison Matrix"
                                                        tableId="comparison-matrix"
                                                    >
                                                        {renderWeightedMatrix()}
                                                    </SmartTable>
                                                </div>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        )}
                                    </Tab.Pane>

                                                                        <Tab.Pane eventKey="elo">
                                        <div className="mb-3">
                                            <Form.Check
                                                type="switch"
                                                id="auto-update-elo"
                                                label="Auto-update ELO when filters change (may be slow with many votes)"
                                                checked={autoUpdateElo}
                                                onChange={(e) => setAutoUpdateElo(e.target.checked)}
                                            />
                                        </div>
                                        <EloLeaderboard
                                            surveyId={surveyId}
                                            activeVariantIds={uniqueCategories.variant
                                                ?.filter(v => v.isActive)
                                                ?.map(v => v.variant) || []}
                                            autoUpdate={autoUpdateElo}
                                        />
                                    </Tab.Pane>
                                </Tab.Content>
                            </Tab.Container>
                        </Col>
                    </Row>
                </>
            : isLoadingWeighted ? (
                <Loading message="Loading Results" />
            ) : comparisonScheme === 'reference_vs_all' ? (
                <ReferenceResults
                    votes={[]}
                    referenceVariantId={referenceVariantId}
                    referenceVariantName={referenceVariantName || 'Reference'}
                    candidateVariants={getReferenceCandidateVariants()}
                />
            ) : (
                <h3 className="text-center">No results available for this survey.</h3>
            )}
        </Container>
    );
};

export default Results;
